import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Eye, Send, EyeOff, CalendarClock, Trash2, Loader2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { http, formatError } from "@/lib/api";
import { StatusBadge, ukDate, ukDateTime, postAction, deletePost, createPost } from "./blogApi";
import { FieldDialog } from "./FieldDialog";

const Btn = ({ icon: I, label, onClick, testid, danger }) => (
  <button onClick={onClick} title={label} aria-label={label} data-testid={testid}
    className={`rounded-lg p-2 transition-colors ${danger ? "text-red-600 hover:bg-red-50" : "text-[#581C87] hover:bg-violet-100"}`}><I className="h-4 w-4" /></button>
);

const Row = ({ p, act, onDelete, onSchedule }) => (
  <tr className="border-t border-violet-100 align-top" data-testid={`blog-row-${p.id}`}>
    <td className="py-3 pr-4">
      <Link to={`/admin/blog/${p.id}`} className="font-bold text-[#13052A] hover:text-[#7C3AED]" data-testid={`blog-row-title-${p.id}`}>{p.title}</Link>
      <div className="text-xs text-slate-500">/blog/{p.slug}{p.draft_saved_at && p.status === "published" ? " · unpublished changes" : ""}</div>
    </td>
    <td className="py-3 pr-4"><StatusBadge status={p.status} testid={`blog-row-status-${p.id}`} />
      {p.status === "scheduled" && <div className="mt-1 text-xs text-blue-800">{ukDateTime(p.publish_at)}</div>}</td>
    <td className="py-3 pr-4 text-sm text-slate-700">{p.category || "-"}</td>
    <td className="py-3 pr-4 text-sm text-slate-700">{ukDate(p.published_at) || "-"}</td>
    <td className="py-3 pr-4 text-sm text-slate-700">{ukDateTime(p.draft_saved_at || p.updated_at)}</td>
    <td className="py-2">
      <div className="flex flex-wrap gap-0.5">
        <Link to={`/admin/blog/${p.id}`} title="Edit" aria-label="Edit" className="rounded-lg p-2 text-[#581C87] hover:bg-violet-100" data-testid={`blog-edit-${p.id}`}><Pencil className="h-4 w-4" /></Link>
        <a href={`/admin/preview/${p.id}`} target="_blank" rel="noreferrer" title="Preview" aria-label="Preview" className="rounded-lg p-2 text-[#581C87] hover:bg-violet-100" data-testid={`blog-preview-${p.id}`}><Eye className="h-4 w-4" /></a>
        {p.status === "published"
          ? <Btn icon={EyeOff} label="Unpublish" onClick={() => act(p, "unpublish")} testid={`blog-unpublish-${p.id}`} />
          : <><Btn icon={Send} label="Publish now" onClick={() => act(p, "publish")} testid={`blog-publish-${p.id}`} />
            <Btn icon={CalendarClock} label="Schedule" onClick={() => onSchedule(p)} testid={`blog-schedule-${p.id}`} /></>}
        <Btn icon={Trash2} label="Delete" danger onClick={() => onDelete(p)} testid={`blog-delete-${p.id}`} />
      </div>
    </td>
  </tr>
);

export const BlogList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [cat, setCat] = useState("");
  const [del, setDel] = useState(null);
  const [sched, setSched] = useState(null);
  const load = useCallback(() => { http.get("/admin/posts").then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const cats = useMemo(() => [...new Set((items || []).map((p) => p.category).filter(Boolean))].sort(), [items]);
  const shown = (items || []).filter((p) => (!q || p.title.toLowerCase().includes(q.toLowerCase())) && (!status || p.status === status) && (!cat || p.category === cat));

  const act = async (p, action) => {
    try {
      const r = await postAction(p.id, action);
      toast.success(action === "publish" ? "Published." : "Unpublished. The article is now a draft.", action === "publish" ? { action: { label: "View article", onClick: () => window.open(`/blog/${r.slug}`, "_blank") } } : undefined);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const newPost = async () => {
    try { const p = await createPost(); navigate(`/admin/blog/${p.id}`); } catch (e) { toast.error(formatError(e)); }
  };
  const confirmDelete = async () => {
    try { await deletePost(del.id); toast.success("Article deleted."); setDel(null); load(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="blog-list-admin">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-extrabold text-[#13052A]">Blog articles</h1>
        <button onClick={newPost} className="btn-accent" data-testid="blog-new-article"><Plus className="h-4 w-4" /> New article</button>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <label className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title" className="w-full rounded-xl border border-violet-200 bg-white py-2.5 pl-9 pr-3 text-sm" data-testid="blog-search" />
        </label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-violet-200 bg-white px-3 py-2.5 text-sm" data-testid="blog-filter-status">
          <option value="">All statuses</option><option value="draft">Draft</option><option value="scheduled">Scheduled</option><option value="published">Published</option>
        </select>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-xl border border-violet-200 bg-white px-3 py-2.5 text-sm" data-testid="blog-filter-category">
          <option value="">All categories</option>{cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="mt-6 overflow-x-auto rounded-[24px] bg-white p-5">
        {!items ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#7C3AED]" /> : (
          <table className="w-full min-w-[860px] text-left">
            <thead><tr className="text-xs font-bold uppercase tracking-widest text-[#581C87]">
              <th className="pb-3">Title</th><th className="pb-3">Status</th><th className="pb-3">Category</th><th className="pb-3">Publication date</th><th className="pb-3">Last edited</th><th className="pb-3">Actions</th>
            </tr></thead>
            <tbody>{shown.map((p) => <Row key={p.id} p={p} act={act} onDelete={setDel} onSchedule={setSched} />)}</tbody>
          </table>
        )}
        {items && !shown.length && <p className="py-8 text-center text-sm text-slate-500" data-testid="blog-list-empty">No articles match.</p>}
      </div>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="blog-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this article?</AlertDialogTitle>
            <AlertDialogDescription>"{del?.title}" will be permanently removed and its web address will show "page not found". This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="blog-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="blog-delete-confirm">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ScheduleDialog post={sched} onClose={() => setSched(null)} onDone={load} />
    </div>
  );
};

export const ScheduleDialog = ({ post, onClose, onDone }) => (
  <FieldDialog open={!!post} onClose={onClose} title="Schedule article" testid="schedule-dialog" submitLabel="Schedule"
    description="Choose when this article goes live. Times are UK time (GMT/BST)."
    fields={[{ key: "local", label: "Publish on (UK time)", type: "datetime-local" }]}
    onSubmit={async ({ local }) => {
      try {
        const r = await postAction(post.id, "schedule", { local });
        toast.success(`Scheduled for ${ukDateTime(r.publish_at)} (UK time).`);
        onDone(r);
      } catch (e) { throw new Error(formatError(e)); }
    }} />
);
